/**
 * BulkCreateCardsDialog — v1.1
 *
 * Tap Engine Cards bulk sequential card creation. Reuses the existing
 * `POST /api/cards/bulk` endpoint (which already loops + skips duplicates).
 * No new subsystem, no separate engine — just a single dialog wrapping
 * the existing creation mechanism.
 *
 * Flow: Inputs → Preview → Confirm → Result toast.
 *
 * Padding width is derived from the literal Start value, so:
 *   start="001", end="050" → TABLE001 … TABLE050  (width 3)
 *   start="1001", end="1025" → MILES1001 … MILES1025  (width 4)
 *
 * Per spec § Duplicate Protection: duplicates are skipped server-side,
 * the whole batch never aborts.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Layers, ChevronRight, ChevronLeft } from 'lucide-react';

import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { cardsAPI } from '../../lib/api';

const MAX_BATCH = 100;
const EXPERIENCE_OPTIONS = ['dine_in', 'pickup', 'delivery'];
// MEDICAL — a clinic is one experience, and it is the only one offered to a
// medical organization. Every other vertical keeps the list above.
const MEDICAL_EXPERIENCE_OPTIONS = ['medical'];
const experienceOptionsFor = (vertical) => (
    vertical === 'medical' ? MEDICAL_EXPERIENCE_OPTIONS : EXPERIENCE_OPTIONS
);
const LANGUAGE_OPTIONS = ['en', 'fr', 'bilingual', 'trilingual'];

const PLATFORM_OPTIONS = [
    'google_reviews', 'facebook_reviews', 'tripadvisor', 'yelp',
    'opentable', 'zomato', 'trustpilot',
];

/**
 * Compute the padded card_id list for the requested range.
 * Returns `{ ids, error }`. Caller decides how to surface the error.
 */
function buildCardIdList({ prefix, start, end }) {
    const trimmedPrefix = (prefix || '').trim();
    if (!trimmedPrefix) return { ids: [], error: 'prefix_required' };

    const startStr = String(start || '').trim();
    const endStr = String(end || '').trim();
    if (!startStr || !endStr) return { ids: [], error: 'range_required' };
    if (!/^\d+$/.test(startStr) || !/^\d+$/.test(endStr)) return { ids: [], error: 'range_not_numeric' };

    const startNum = parseInt(startStr, 10);
    const endNum = parseInt(endStr, 10);
    if (endNum < startNum) return { ids: [], error: 'range_inverted' };

    const count = endNum - startNum + 1;
    if (count > MAX_BATCH) return { ids: [], error: 'range_exceeds_max' };

    // Padding width derives from the literal Start value — leading zeros honoured.
    const width = startStr.length;
    const ids = [];
    for (let i = startNum; i <= endNum; i += 1) {
        ids.push(`${trimmedPrefix}${String(i).padStart(width, '0')}`);
    }
    return { ids, error: null };
}

const BulkCreateCardsDialog = ({
    open,
    onOpenChange,
    organizationId,
    organizations,
    locations,
    defaultLocationId,   // preselected from page-level location filter (spec: option b)
    onCreated,           // (createdCards, requestedCount) → void
}) => {
    const { t } = useTranslation();

    // Step state — single linear flow: 'inputs' → 'preview' → submitting
    const [step, setStep] = useState('inputs');
    const [submitting, setSubmitting] = useState(false);

    // Form fields
    const [prefix, setPrefix] = useState('');
    const [startValue, setStartValue] = useState('');
    const [endValue, setEndValue] = useState('');
    const [orgId, setOrgId] = useState(organizationId || '');
    const [locId, setLocId] = useState(defaultLocationId || '__unassigned__');
    const [experienceType, setExperienceType] = useState('dine_in');
    const [platform, setPlatform] = useState('__default__');
    const [languageMode, setLanguageMode] = useState('__default__');

    // Reset state every time the dialog reopens. Preselect location from the
    // page filter when one is active (spec: b → fallback to c).
    useEffect(() => {
        if (open) {
            setStep('inputs');
            setPrefix('');
            setStartValue('');
            setEndValue('');
            setOrgId(organizationId || (organizations?.[0]?.id || ''));
            setLocId(defaultLocationId || '__unassigned__');
            setExperienceType('dine_in');
            setPlatform('__default__');
            setLanguageMode('__default__');
        }
    }, [open, organizationId, defaultLocationId, organizations]);

    // Live preview of generated IDs + any validation error.
    const { ids, error } = useMemo(
        () => buildCardIdList({ prefix, start: startValue, end: endValue }),
        [prefix, startValue, endValue],
    );

    const orgRecord = organizations?.find((o) => o.id === orgId);
    const locRecord = locations?.find((l) => l.id === locId);

    // The experience list follows the SELECTED organization's vertical, and it
    // re-selects itself the moment that organization changes, so a medical
    // clinic can never be created as Dine-In.
    const experienceOptions = experienceOptionsFor(orgRecord?.vertical);
    useEffect(() => {
        if (!experienceOptions.includes(experienceType)) {
            setExperienceType(experienceOptions[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, organizations]);

    // Memoize the org-scoped location list so the Select dropdown doesn't
    // re-filter on every keystroke in prefix/start/end fields.
    const scopedLocations = useMemo(
        () => (locations || []).filter((l) => !orgId || l.organization_id === orgId),
        [locations, orgId],
    );

    const errorMessage = (() => {
        if (!error) return null;
        const map = {
            prefix_required: t('bulkCreate.errors.prefixRequired', 'Prefix is required.'),
            range_required: t('bulkCreate.errors.rangeRequired', 'Start and End are required.'),
            range_not_numeric: t('bulkCreate.errors.rangeNotNumeric', 'Start and End must be numeric.'),
            range_inverted: t('bulkCreate.errors.rangeInverted', 'End must be greater than or equal to Start.'),
            range_exceeds_max: t('bulkCreate.errors.rangeExceedsMax', { defaultValue: 'Maximum {{max}} cards per batch.', max: MAX_BATCH }),
        };
        return map[error] || error;
    })();

    const canProceed = !error && ids.length > 0 && !!orgId;

    const goToPreview = () => {
        if (!canProceed) return;
        setStep('preview');
    };

    const handleConfirm = async () => {
        if (!canProceed) return;
        setSubmitting(true);
        try {
            const payload = {
                card_ids: ids,
                organization_id: orgId,
                experience_type: experienceType,
            };
            if (locId && locId !== '__unassigned__') payload.location_id = locId;
            if (platform && platform !== '__default__') payload.platform = platform;
            if (languageMode && languageMode !== '__default__') payload.language_mode = languageMode;

            const res = await cardsAPI.createBulk(payload);
            const created = Array.isArray(res?.data) ? res.data : [];
            const requested = ids.length;
            const skipped = requested - created.length;

            if (skipped > 0) {
                toast.success(
                    t('bulkCreate.resultPartial', {
                        defaultValue: '{{created}} created · {{skipped}} skipped (duplicates)',
                        created: created.length,
                        skipped,
                    }),
                );
            } else {
                toast.success(
                    t('bulkCreate.resultAll', {
                        defaultValue: '{{count}} cards created',
                        count: created.length,
                    }),
                );
            }
            if (onCreated) onCreated(created, requested);
            onOpenChange(false);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Bulk create failed';
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    const platformLabel = platform === '__default__'
        ? t('cards.useOrgDefault', 'Use organization default')
        : t(`platforms.${platform}`, platform);
    const langLabel = languageMode === '__default__'
        ? t('cards.useOrgDefault', 'Use organization default')
        : t(`languageMode.${languageMode}`, languageMode);
    const expEmoji = { dine_in: '🍽', pickup: '🥡', delivery: '🚗' }[experienceType] || '';
    const expLabel = t(`reviewShield.experience.${experienceType}`, experienceType);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[640px]"
                data-testid="bulk-create-dialog"
                data-insight-id="tap-engine-bulk-create"
                data-insight-label="Bulk create Tap Engine cards"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Layers className="w-5 h-5" />
                        {t('bulkCreate.title', 'Bulk Create Cards')}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'inputs'
                            ? t('bulkCreate.subtitle', 'Generate a sequential range of cards in one operation. Up to {{max}} per batch.', { max: MAX_BATCH })
                            : t('bulkCreate.confirmSubtitle', 'Review the batch and confirm before cards are created.')}
                    </DialogDescription>
                </DialogHeader>

                {step === 'inputs' && (
                    <div className="space-y-4 py-2">
                        {/* Prefix + Range */}
                        <div className="grid grid-cols-3 gap-3" data-insight-id="tap-engine-bulk-prefix">
                            <div>
                                <Label className="mb-1.5 block">{t('bulkCreate.prefix', 'Prefix')}</Label>
                                <Input
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value)}
                                    placeholder="MILES"
                                    data-testid="bulk-prefix"
                                    autoFocus
                                />
                            </div>
                            <div data-insight-id="tap-engine-bulk-range">
                                <Label className="mb-1.5 block">{t('bulkCreate.start', 'Start')}</Label>
                                <Input
                                    value={startValue}
                                    onChange={(e) => setStartValue(e.target.value)}
                                    placeholder="1001"
                                    inputMode="numeric"
                                    data-testid="bulk-start"
                                />
                            </div>
                            <div>
                                <Label className="mb-1.5 block">{t('bulkCreate.end', 'End')}</Label>
                                <Input
                                    value={endValue}
                                    onChange={(e) => setEndValue(e.target.value)}
                                    placeholder="1025"
                                    inputMode="numeric"
                                    data-testid="bulk-end"
                                />
                            </div>
                        </div>

                        {/* Live preview line + error */}
                        <div className="text-sm flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
                            <span className="text-muted-foreground">
                                {ids.length > 0 ? (
                                    <>
                                        <span className="font-mono">{ids[0]}</span>
                                        {' → '}
                                        <span className="font-mono">{ids[ids.length - 1]}</span>
                                    </>
                                ) : (
                                    <span>{t('bulkCreate.previewPlaceholder', 'Range preview will appear here')}</span>
                                )}
                            </span>
                            <span className="text-foreground font-medium" data-testid="bulk-count">
                                {ids.length > 0 ? t('bulkCreate.countN', { defaultValue: '{{count}} cards', count: ids.length }) : ''}
                            </span>
                        </div>
                        {errorMessage && (
                            <p className="text-sm text-destructive" data-testid="bulk-error">{errorMessage}</p>
                        )}

                        {/* Organization */}
                        <div>
                            <Label className="mb-1.5 block">{t('cards.organization', 'Organization')}</Label>
                            <Select value={orgId} onValueChange={setOrgId}>
                                <SelectTrigger data-testid="bulk-organization">
                                    <SelectValue placeholder={t('cards.selectOrganization', 'Select organization')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {(organizations || []).map((o) => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Location (optional) */}
                        <div>
                            <Label className="mb-1.5 block">
                                {t('cards.location', 'Location')}
                                <span className="text-muted-foreground text-xs ml-2">
                                    ({t('common.optional', 'optional')})
                                </span>
                            </Label>
                            <Select value={locId} onValueChange={setLocId}>
                                <SelectTrigger data-testid="bulk-location">
                                    <SelectValue placeholder={t('cards.selectLocation', 'Select location')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__unassigned__">
                                        {t('bulkCreate.unassigned', 'Unassigned (assign later)')}
                                    </SelectItem>
                                    {scopedLocations.map((l) => (
                                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Experience + Platform + Language */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label className="mb-1.5 block">{t('reviewShield.experience.label', 'Experience')}</Label>
                                <Select value={experienceType} onValueChange={setExperienceType}>
                                    <SelectTrigger data-testid="bulk-experience">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {experienceOptions.map((e) => (
                                            <SelectItem key={e} value={e} data-testid={`bulk-experience-opt-${e}`}>
                                                {t(`reviewShield.experience.${e}_emoji`)} {t(`reviewShield.experience.${e}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-1.5 block">{t('cards.platform', 'Platform')}</Label>
                                <Select value={platform} onValueChange={setPlatform}>
                                    <SelectTrigger data-testid="bulk-platform">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__default__">{t('cards.useOrgDefault', 'Use organization default')}</SelectItem>
                                        {PLATFORM_OPTIONS.map((p) => (
                                            <SelectItem key={p} value={p}>{t(`platforms.${p}`, p)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-1.5 block">{t('cardSettings.languageMode', 'Language')}</Label>
                                <Select value={languageMode} onValueChange={setLanguageMode}>
                                    <SelectTrigger data-testid="bulk-language">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__default__">{t('cards.useOrgDefault', 'Use organization default')}</SelectItem>
                                        {LANGUAGE_OPTIONS.map((l) => (
                                            <SelectItem key={l} value={l}>{t(`languageMode.${l}`, l)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {step === 'preview' && (
                    <div className="space-y-3 py-2" data-insight-id="tap-engine-bulk-preview">
                        <div className="rounded-lg border border-border divide-y divide-border">
                            <PreviewRow label={t('bulkCreate.range', 'Range')} value={
                                <span className="font-mono">{ids[0]} → {ids[ids.length - 1]}</span>
                            } />
                            <PreviewRow label={t('bulkCreate.cards', 'Cards')} value={
                                <span data-testid="bulk-preview-count">{ids.length}</span>
                            } />
                            <PreviewRow label={t('reviewShield.experience.label', 'Experience')} value={`${expEmoji} ${expLabel}`} />
                            <PreviewRow label={t('cards.platform', 'Platform')} value={platformLabel} />
                            <PreviewRow label={t('cardSettings.languageMode', 'Language')} value={langLabel} />
                            <PreviewRow label={t('cards.organization', 'Organization')} value={orgRecord?.name || '—'} />
                            <PreviewRow label={t('cards.location', 'Location')} value={
                                locId === '__unassigned__'
                                    ? <em className="text-muted-foreground">{t('bulkCreate.unassigned', 'Unassigned (assign later)')}</em>
                                    : (locRecord?.name || '—')
                            } />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('bulkCreate.duplicateNotice', 'Existing card IDs in this range will be skipped — the batch will not abort.')}
                        </p>
                    </div>
                )}

                <DialogFooter className="flex sm:justify-between gap-2">
                    {step === 'inputs' && (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel-btn">
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button
                                onClick={goToPreview}
                                disabled={!canProceed}
                                data-testid="bulk-next-btn"
                            >
                                {t('bulkCreate.next', 'Preview')}
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </>
                    )}
                    {step === 'preview' && (
                        <>
                            <Button variant="outline" onClick={() => setStep('inputs')} disabled={submitting} data-testid="bulk-back-btn">
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                {t('common.back', 'Back')}
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                disabled={submitting}
                                data-testid="bulk-confirm-btn"
                                data-insight-id="tap-engine-bulk-confirm"
                            >
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {t('bulkCreate.confirm', { defaultValue: 'Create {{count}} Cards', count: ids.length })}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const PreviewRow = ({ label, value }) => (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium text-foreground text-right">{value}</span>
    </div>
);

export default BulkCreateCardsDialog;
export { buildCardIdList, MAX_BATCH };
